import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Building2,
  Search,
  ToggleLeft,
  ToggleRight,
  Key,
  Eye,
  Star,
  TriangleAlert,
  ShieldAlert,
  CheckCircle2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAgencies() {
  const { getAuthHeader } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [trustDialog, setTrustDialog] = useState(null);
  const [trustAction, setTrustAction] = useState('warn'); // warn, deactivate, activate

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/agencies`, {
        headers: getAuthHeader()
      });
      setAgencies(response.data.agencies);
    } catch (error) {
      console.error('Failed to fetch agencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (agencyId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/agencies/${agencyId}/status`,
        { is_active: !currentStatus },
        { headers: getAuthHeader() }
      );
      setAgencies(prev => prev.map(a =>
        a.id === agencyId ? { ...a, is_active: !currentStatus } : a
      ));
      toast.success(`Agency ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleVerification = async (agencyId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/agencies/${agencyId}/verify`,
        null,
        {
          params: { action: currentStatus ? 'reject' : 'approve' },
          headers: getAuthHeader()
        }
      );
      setAgencies(prev => prev.map(a =>
        a.id === agencyId ? { ...a, is_verified: !currentStatus } : a
      ));
      toast.success(`Agency verification ${!currentStatus ? 'approved' : 'removed'}`);
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleTrustAction = async () => {
    if (!trustDialog) return;

    try {
      await axios.put(`${API_URL}/api/admin/agencies/${trustDialog.id}/trust-action`, {
        action: trustAction
      }, { headers: getAuthHeader() });

      toast.success(`Action '${trustAction}' applied successfully`);

      // Update local state if needed (e.g. if deactivated)
      if (trustAction === 'deactivate') {
        setAgencies(prev => prev.map(a => a.id === trustDialog.id ? { ...a, is_active: false } : a));
      } else if (trustAction === 'activate') {
        setAgencies(prev => prev.map(a => a.id === trustDialog.id ? { ...a, is_active: true } : a));
      }

      setTrustDialog(null);
    } catch (error) {
      toast.error('Failed to apply trust action');
    }
  };

  const updatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/admin/agencies/${passwordDialog}/password`,
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success('Password updated');
      setPasswordDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const filteredAgencies = agencies.filter(a =>
    a.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-agencies">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Manage Agencies</h1>
        <p className="text-navy-600">{agencies.length} agencies registered</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
        <Input
          placeholder="Search by company name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Agencies List */}
      <div className="space-y-4">
        {filteredAgencies.map((agency) => (
          <Card key={agency.id} data-testid={`agency-card-${agency.id}`}>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-navy-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-navy-900">{agency.company_name}</h3>
                      <Badge className={agency.is_active ? 'bg-green-500' : 'bg-red-500'}>
                        {agency.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {agency.is_verified && (
                        <Badge className="bg-sky-500 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Verified
                        </Badge>
                      )}
                      <Badge className={
                        agency.subscription === 'premium' ? 'bg-purple-500' :
                          agency.subscription === 'basic' ? 'bg-blue-500' : 'bg-gray-500'
                      }>
                        {agency.subscription}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-1 border-yellow-500 text-yellow-700 bg-yellow-50">
                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                        {agency.avg_trust_rating?.toFixed(1) || 'N/A'}
                      </Badge>
                      {(agency.avg_trust_rating && agency.avg_trust_rating <= 3.0) && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <TriangleAlert className="w-3 h-3" /> Risk
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-navy-600">{agency.email}</p>
                    <p className="text-sm text-navy-500">RERA: {agency.rera_number}</p>
                    <p className="text-xs text-navy-400 mt-1">
                      Joined: {new Date(agency.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`/agency/${agency.slug}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTrustDialog(agency)}
                    className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                  >
                    <ShieldAlert className="w-4 h-4" />
                    Trust
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPasswordDialog(agency.id)}
                    className="gap-2"
                    data-testid={`password-btn-${agency.id}`}
                  >
                    <Key className="w-4 h-4" />
                    Password
                  </Button>
                  <Button
                    variant={agency.is_active ? 'destructive' : 'default'}
                    size="sm"
                    onClick={() => toggleStatus(agency.id, agency.is_active)}
                    className="gap-2"
                    data-testid={`toggle-btn-${agency.id}`}
                  >
                    {agency.is_active ? (
                      <>
                        <ToggleRight className="w-4 h-4" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-4 h-4" />
                        Activate
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleVerification(agency.id, agency.is_verified)}
                    className={`gap-2 ${agency.is_verified ? 'text-amber-600 border-amber-200' : 'text-sky-600 border-sky-200'}`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {agency.is_verified ? 'Unverify' : 'Verify'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Password Dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={() => setPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="New password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPasswordDialog(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={updatePassword} className="flex-1 bg-navy-900 hover:bg-navy-800">
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trust Action Dialog */}
      <Dialog open={!!trustDialog} onOpenChange={() => setTrustDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Agency Trust</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-navy-600">
              Agency: <span className="font-semibold">{trustDialog?.company_name}</span><br />
              Current Rating: <span className="font-semibold">{trustDialog?.avg_trust_rating?.toFixed(1) || 'N/A'}</span>
            </p>

            <div className="space-y-2">
              <label className="text-sm font-medium text-navy-900">Action</label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={trustAction}
                onChange={(e) => setTrustAction(e.target.value)}
              >
                <option value="warn">Send Warning Email</option>
                <option value="deactivate">Deactivate Account</option>
                <option value="activate">Activate Account</option>
              </select>
            </div>

            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={() => setTrustDialog(null)}>
                Cancel
              </Button>
              <Button onClick={handleTrustAction} className="bg-navy-900 hover:bg-navy-800">
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
